import React, { useMemo } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ReferralData, DeduplicationMatch } from '../types';
import PatientInfoGrid from './PatientInfoGrid';
import DeduplicationTable from './DeduplicationTable';
// import CommentsSection from './CommentsSection';
import AuditChangesSection from './AuditChangesSection';
import CaregiverSection from './CaregiverSection';
import ProcessedFullProfileExpandedSections from './ProcessedFullProfileExpandedSections';
import TrackingParametersSection from './TrackingParametersSection';
import CustomAccordion from '../ui/CustomAccordion';
import { ExpandedFieldConfig } from '../Table';
import { STATUS } from '../../LeadManagement/constants';
import { PatientProfileTabFields } from '../../LeadManagement/LeadManagement.types';
import {
  DEDUPLICATION_BATCH_ALL_FAILED_MESSAGE,
  normalizeLeadIdsArg,
  useGetCCPatientsByIdsQuery,
  useGetAuditHistoryQuery,
  useGetTrackingParametersByEnrollmentIdQuery,
} from '../../../features/lma/ccPatientApi';

interface ExpandedRowContentProps {
  row: ReferralData;
  expandedSection: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null;
  setExpandedSection: (
    section: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null
  ) => void;
  newComment: string;
  setNewComment: (comment: string) => void;
  onOpenMergeModal?: (match: DeduplicationMatch | undefined, row: ReferralData) => void;
  onSendToCC?: () => void;
  isSending?: boolean;
  selectedDedupeRecord: string;
  setSelectedDedupeRecord: (id: string) => void;
  hideDedupeSelectColumn?: boolean;
  isProcessedTab?: boolean;
  expandedConfig?: ExpandedFieldConfig;
  statusValue?: string;
  // Flag Patient Props
  onFlagPatient?: () => Promise<void>;
  currentUser?: string;
  lockedRecordsCount?: number;
  isLockingEnabled?: boolean;
  currentTabKey?: string;
  PatientProfileConfig?: PatientProfileTabFields;
}

const ExpandedRowContent: React.FC<ExpandedRowContentProps> = ({
  row,
  expandedSection,
  setExpandedSection,
  // newComment,
  // setNewComment,
  onOpenMergeModal,
  onSendToCC,
  isSending = false,
  selectedDedupeRecord,
  setSelectedDedupeRecord,
  hideDedupeSelectColumn = false,
  isProcessedTab = false,
  expandedConfig,
  statusValue,
  onFlagPatient,
  currentUser,
  lockedRecordsCount = 0,
  isLockingEnabled = process.env.NEXT_PUBLIC_LMA_LOCK_ENABLED === 'true',
  currentTabKey,
  PatientProfileConfig,
}) => {
  // Extract all CC Patient IDs from deduplication matches
  const ccPatientIds = useMemo(() => {
    return (row.deduplication || [])
      .map((match) => match.ccPatientReferenceId)
      .filter((id): id is string => !!id);
  }, [row.deduplication]);

  // Fetch CC Patient data using RTK Query
  const shouldFetch =
    expandedSection === 'deduplication' && ccPatientIds.length > 0 && !isProcessedTab;

  const {
    data: ccPatientsData,
    isLoading: isLoadingDedupe,
    error: dedupeError,
    refetch: refetchCCPatients,
  } = useGetCCPatientsByIdsQuery(ccPatientIds, {
    skip: !shouldFetch,
  });

  const auditLeadIds: number[] = useMemo(() => {
    const fromRow = normalizeLeadIdsArg(row.leadId);
    const fromDetails =
      row.leadDetails?.map((d) => d.leadId).filter((id): id is number => Number.isFinite(id)) ?? [];
    const merged = [...new Set([...fromRow, ...fromDetails])];
    return merged.length > 0 ? merged : fromRow;
  }, [row.leadId, row.leadDetails]);

  const isProcessedFullProfileExpanded =
    isProcessedTab && Array.isArray(row.leadDetails) && row.leadDetails.length > 0;

  const { data: getAuditHistoryData } = useGetAuditHistoryQuery(
    { leadIdsArg: auditLeadIds, tabKey: currentTabKey },
    { skip: auditLeadIds.length === 0 }
  );

  // Fetch tracking parameters
  const trackingEnrollmentId = useMemo(() => {
    if (!row.enrollmentId) return '';
    return (
      String(row.enrollmentId)
        .split(',')
        .map((v) => v.trim())
        .find(Boolean) || ''
    );
  }, [row.enrollmentId]);

  const { data: trackingParamsData } = useGetTrackingParametersByEnrollmentIdQuery(
    trackingEnrollmentId,
    {
      skip: !trackingEnrollmentId,
    }
  );

  // Merge CC Patient data with deduplication matches
  const enrichedDeduplicationData = useMemo(() => {
    if (!ccPatientsData || !row.deduplication) {
      return [];
    }

    return row.deduplication.map((dedupeMatch) => {
      const ccData = ccPatientsData[dedupeMatch.ccPatientReferenceId];
      return {
        ...dedupeMatch,
        ...(ccData || {}),
      };
    });
  }, [row.deduplication, ccPatientsData]);

  return (
    <Box sx={{ margin: 1.25 }}>
      {/* Patient and Caregiver Information Grid � Processed full-profile uses nested accordions */}
      {isProcessedFullProfileExpanded ? (
        <ProcessedFullProfileExpandedSections
          row={row}
          onSendToCC={onSendToCC}
          isSending={isSending}
          selectedDedupeRecord={selectedDedupeRecord}
          expandedConfig={expandedConfig}
          statusValue={statusValue}
          onFlagPatient={onFlagPatient}
          currentUser={currentUser}
          lockedRecordsCount={lockedRecordsCount}
          isLockingEnabled={isLockingEnabled}
          currentTabKey={currentTabKey}
          PatientProfileConfig={PatientProfileConfig}
          caregiverHeaderText={expandedConfig?.caregiverInfoHeaderText}
        />
      ) : (
        <>
          <PatientInfoGrid
            row={row}
            onSendToCC={onSendToCC}
            isSending={isSending}
            selectedDedupeRecord={selectedDedupeRecord}
            isProcessedTab={isProcessedTab}
            fieldConfig={expandedConfig?.personal}
            personalInfoHeaderText={expandedConfig?.personalInfoHeaderText}
            statusValue={statusValue}
            viewDetails={expandedConfig?.viewDetails}
            sendToCC={expandedConfig?.sendToCC}
            onFlagPatient={onFlagPatient}
            currentUser={currentUser}
            lockedRecordsCount={lockedRecordsCount}
            isLockingEnabled={isLockingEnabled}
            currentTabKey={currentTabKey}
            cdpUiMode="enrollment"
          />

          <CaregiverSection
            caregiver={row.caregiver}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            fieldConfig={expandedConfig?.caregiverInfo}
            headerText={expandedConfig?.caregiverInfoHeaderText}
          />

          {/* Tracking Parameters Accordion */}
          <TrackingParametersSection
            trackingParams={trackingParamsData || {}}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            fieldConfig={expandedConfig?.trackingParams}
            headerText={expandedConfig?.trackingParamsHeaderText}
          />
        </>
      )}

      {/* Deduplication Additional Parameters - Only show if status is not "New-Record" and not on Processed tab */}
      {row.deduplication &&
        row.deduplication.length > 0 &&
        !isProcessedTab &&
        statusValue !== STATUS.NEW_RECORD &&
        statusValue !== STATUS.RETURNING_USER && (
          <CustomAccordion
            title={
              expandedConfig?.dedupAddParamsHeaderText || 'Deduplication Additional Parameters'
            }
            expanded={expandedSection === 'deduplication'}
            onChange={() =>
              setExpandedSection(expandedSection === 'deduplication' ? null : 'deduplication')
            }
            summaryActions={
              <Tooltip title="Refresh deduplication data" placement="top" arrow>
                <IconButton
                  size="small"
                  disabled={isLoadingDedupe}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    refetchCCPatients();
                  }}
                  aria-label="Refresh deduplication data"
                  sx={{
                    color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                    '&:hover': {
                      backgroundColor: (theme: any) =>
                        theme.palette.themeColor?.main
                          ? `${theme.palette.themeColor.main}14`
                          : 'rgba(37, 94, 94, 0.08)',
                    },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            }
            detailsSx={{ p: '0!important' }}
          >
            {isLoadingDedupe ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading deduplication details...
                </Typography>
              </Box>
            ) : dedupeError ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mx: 'auto', maxWidth: 680, whiteSpace: 'pre-line' }}
                >
                  {typeof dedupeError === 'object' && 'data' in dedupeError
                    ? String(dedupeError.data)
                    : DEDUPLICATION_BATCH_ALL_FAILED_MESSAGE}
                </Typography>
              </Box>
            ) : enrichedDeduplicationData.length > 0 ? (
              <DeduplicationTable
                deduplicationMatches={enrichedDeduplicationData}
                onOpenMergeModal={onOpenMergeModal}
                selectedRecordId={selectedDedupeRecord}
                onRecordSelect={setSelectedDedupeRecord}
                hideSelectColumn={hideDedupeSelectColumn}
                fieldConfig={expandedConfig?.dedupAddParams}
                parentData={{
                  phoneNumber: row.phone, // to match with dedupe row phone
                  deduplication: row.deduplication,
                }}
              />
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No deduplication matches found.
                </Typography>
              </Box>
            )}
          </CustomAccordion>
        )}

      {/* Comments Section */}
      {/* <CommentsSection
        comments={row.comments}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
        newComment={newComment}
        setNewComment={setNewComment}
        headerText={expandedConfig?.commentsHeaderText}
      /> */}

      {(() => {
        const auditChanges = getAuditHistoryData ?? row.auditChanges;
        return auditChanges && auditChanges.length > 0 ? (
          <AuditChangesSection
            auditChanges={auditChanges}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            fieldConfig={expandedConfig?.auditChanges}
            resetFilterText={expandedConfig?.resetFilterText}
          />
        ) : null;
      })()}
    </Box>
  );
};

export default ExpandedRowContent;
